import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_ReferenceBrowserSupport() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🌐 CSS Reference: Browser Support</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        Not all CSS properties are supported in every browser. Modern features
        like <code>grid</code>, <code>flexbox</code>, or{" "}
        <code>backdrop-filter</code>
        may behave differently depending on the browser version. It’s important
        to check compatibility and provide fallbacks.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#1e3a8a" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Use{" "}
            <a href="https://caniuse.com" target="_blank" rel="noreferrer">
              Can I Use
            </a>{" "}
            to check browser support.
          </li>
          <li>
            Modern properties may need <b>vendor prefixes</b> (e.g.,{" "}
            <code>-webkit-</code>).
          </li>
          <li>
            Older browsers may ignore unsupported properties without breaking
            layout.
          </li>
          <li>
            Always provide <b>fallback styles</b> for unsupported features.
          </li>
          <li>
            Progressive enhancement ensures basic styling works everywhere.
          </li>
        </ul>
      </div>

      {/* Example 1 - Flexbox */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Flexbox Support</h2>
        <p>
          Flexbox is well supported in all modern browsers, but some old
          versions need prefixes.
        </p>
        <CodeCopyBlock
          code={`.container {
  display: -webkit-box;   /* Old Safari, iOS */
  display: -ms-flexbox;   /* IE 10 */
  display: flex;          /* Modern browsers */
}`}
        />
        <div
          style={{
            display: "flex",
            gap: "10px",
            marginTop: "1rem",
          }}
        >
          <div
            style={{
              background: "#3b82f6",
              color: "white",
              padding: "10px",
              borderRadius: "6px",
            }}
          >
            Item 1
          </div>
          <div
            style={{
              background: "#10b981",
              color: "white",
              padding: "10px",
              borderRadius: "6px",
            }}
          >
            Item 2
          </div>
          <div
            style={{
              background: "#f59e0b",
              color: "white",
              padding: "10px",
              borderRadius: "6px",
            }}
          >
            Item 3
          </div>
        </div>
      </div>

      {/* Example 2 - CSS Grid */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ CSS Grid Support</h2>
        <p>
          CSS Grid is supported in all modern browsers, but IE 11 has only
          partial support.
        </p>
        <CodeCopyBlock
          code={`.grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
}`}
        />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: "10px",
            marginTop: "1rem",
          }}
        >
          <div
            style={{
              background: "#3b82f6",
              padding: "20px",
              borderRadius: "6px",
              color: "white",
            }}
          >
            A
          </div>
          <div
            style={{
              background: "#10b981",
              padding: "20px",
              borderRadius: "6px",
              color: "white",
            }}
          >
            B
          </div>
          <div
            style={{
              background: "#f59e0b",
              padding: "20px",
              borderRadius: "6px",
              color: "white",
            }}
          >
            C
          </div>
        </div>
      </div>

      {/* Example 3 - Backdrop Filter */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Backdrop Filter (Limited Support)</h2>
        <p>
          <code>backdrop-filter</code> is only supported in Safari and
          Chromium-based browsers. Firefox requires enabling a flag.
        </p>
        <CodeCopyBlock
          code={`.frosted {
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px); /* Safari */
}`}
        />
        <div
          style={{
            position: "relative",
            backgroundImage: "url('https://picsum.photos/400/200?grayscale')",
            backgroundSize: "cover",
            padding: "20px",
            borderRadius: "8px",
            marginTop: "1rem",
            color: "white",
          }}
        >
          <div
            style={{
              backdropFilter: "blur(6px)",
              WebkitBackdropFilter: "blur(6px)",
              padding: "10px",
              background: "rgba(0,0,0,0.3)",
              borderRadius: "6px",
            }}
          >
            Backdrop Filter Example
          </div>
        </div>
      </div>

      {/* Example 4 - Fallbacks */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Fallback Example</h2>
        <p>
          Provide fallback values to ensure older browsers still render
          something usable.
        </p>
        <CodeCopyBlock
          code={`.box {
  background: red;                  /* fallback */
  background: linear-gradient(red, orange); /* modern */
}`}
        />
        <div
          style={{
            width: "200px",
            height: "100px",
            background: "linear-gradient(red, orange)",
            borderRadius: "8px",
            marginTop: "1rem",
          }}
        />
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Use{" "}
            <a href="https://caniuse.com" target="_blank" rel="noreferrer">
              caniuse.com
            </a>{" "}
            before using new CSS properties.
          </li>
          <li>
            Include <b>vendor prefixes</b> when targeting older browsers.
          </li>
          <li>
            Provide <b>fallback styles</b> to avoid breaking layouts.
          </li>
          <li>
            Use <b>@supports</b> feature queries to apply styles conditionally.
          </li>
        </ul>
      </div>
    </div>
  );
}
